import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Skeleton,
  Alert,
  Paper,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { DataSourceHealthCard } from '../../components/health/DataSourceHealthCard';
import { SyncHistoryTable } from '../../components/health/SyncHistoryTable';
import { dataHealthService } from '../../services/dataHealthService';
import { useSiteStore } from '../../store/site.store';
import type { DataSourceType } from '../../types/dataHealth.types';

export const DataHealthDashboard: React.FC = () => {
  const queryClient = useQueryClient();

  // Get current site from global store
  const selectedSite = useSiteStore(state => state.selectedSite);
  const currentSiteId = selectedSite?.id;

  // Fetch health data with auto-refresh every minute
  // DISABLED: Endpoint /api/v1/health/sources not yet implemented
  /*
  const { data: healthData, isLoading: healthLoading, error: healthError } = useQuery({
    queryKey: ['dataSourceHealth', currentSiteId],
    queryFn: () => dataHealthService.getDataSourcesHealth(currentSiteId),
    enabled: !!currentSiteId,
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });
  */
  const healthData: any[] = [];
  const healthLoading = false;
  const healthError = null;

  // Fetch sync history
  const { data: syncHistory, isLoading: historyLoading, error: historyError } = useQuery({
    queryKey: ['syncHistory'],
    queryFn: () => dataHealthService.getSyncHistory({ limit: 50 }),
    refetchInterval: 60000,
    retry: 2,
    staleTime: 30000
  });

  // Force sync mutation
  const forceSyncMutation = useMutation({
    mutationFn: (sourceType: string) => dataHealthService.forceSyncDataSource(sourceType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataSourceHealth'] });
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    }
  });

  const handleForceSync = (sourceType: DataSourceType) => {
    forceSyncMutation.mutate(sourceType);
  };

  if (healthError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load data source health information. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Source Health
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Monitor the operational health of data pipelines for GSC, Bing, GA4, and crawl logs.
      </Typography>

      {/* Page Purpose Description - TODO: Refine text later */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main' }}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <InfoIcon sx={{ fontSize: 20, mt: 0.25 }} color="info" />
          <Box component="span">
            <Box component="span" sx={{ fontWeight: 700 }}>Data Health Monitoring:</Box> This is a health monitoring endpoint, not a data ingestion endpoint. 
            It checks the health and status of data you already have in the <code>sync_job_history</code> table, 
            not fetching new data. Use this page to monitor sync job status, identify data lag issues, 
            and troubleshoot pipeline failures. To configure data sources, visit <strong>WebTools Configuration</strong>.
          </Box>
        </Typography>
      </Paper>

      {/* Health Cards Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {healthLoading ? (
          // Loading skeletons
          Array.from({ length: 4 }).map((_, idx) => (
            <Grid item xs={12} sm={6} lg={3} key={idx}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
            </Grid>
          ))
        ) : healthData && healthData.length > 0 ? (
          healthData.map((health) => (
            <Grid item xs={12} sm={6} lg={3} key={health.sourceType}>
              <DataSourceHealthCard
                health={health}
                onForceSync={handleForceSync}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Alert severity="info">
              No data sources configured yet. Health monitoring will appear here once data sources are connected.
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Sync History Table */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Recent Sync Jobs
      </Typography>
      {historyLoading ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      ) : historyError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load sync history.
        </Alert>
      ) : (
        <SyncHistoryTable history={syncHistory || []} />
      )}
    </Box>
  );
};
